import { Component, inject, ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { ResourceInput } from "@fullcalendar/angular-scheduler";
import { TreeNode } from '@openng/optimus-ui/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { ChangeSummary } from 'src/modules/enaio/netgen/ChangeSummary';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from 'src/modules/utils/shared/objects';
import { Strings } from 'src/modules/utils/shared/strings';
import { Utils } from 'src/modules/utils/shared/utils';
import { FullCalendarWrapper } from '../../models/full-calendar-wrapper.model';
import { Project } from '../../netgen/Project';
import { ProjectDto } from '../../netgen/ProjectDto';
import { ProjectPlanningService } from '../../services/project-planning.service';

@Component({
    selector: 'app-planning-view-projects2',
    templateUrl: './planning-view-projects2.component.html',
    styleUrl: './planning-view-projects2.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PlanningViewProjects2Component extends ComponentView {

    service = inject(ProjectPlanningService);

    projects: Project[] = [];
    dtos: ProjectDto[] = [];

    calendarWrapper: FullCalendarWrapper;

    tree: TreeNode[];
    form: FrontendForm;
    resources: ResourceInput[] = [{ id: "", title: "", children: [] }];

    selectedProjects: TreeNode[] = [];
    wasOpened = false;

    @ViewChild("fc") 
    fc: FullCalendarComponent;

    async initParams(): Promise<boolean> {
        this.calendarWrapper = new FullCalendarWrapper(true, true, "project", "type", this.projectsChanged.bind(this), this, this.app, this.service);

        await this.dataChanged();

        this.form = new FrontendForm([
            new FrontendField("statusActive", this.app.t("ProjectPlanning.active"), FrontendFieldType.bool, { 
                value: true,
                onValueChanged: this.projectsChangedInternal.bind(this)
            }),
            new FrontendField("statusFinished", this.app.t("ProjectPlanning.finished"), FrontendFieldType.bool, {
                onValueChanged: this.projectsChangedInternal.bind(this)
            })            
        ]);

        this.addSubscription(this.app.registerChangesCallback(this.serviceChanged.bind(this), "PlanningViewProjects2"));

        return true;
    }

    async serviceChanged(summary: ChangeSummary): Promise<void> {
        if (summary.hasAnyType(["Project", "ProjectType", "CalendarEvent", "CalendarEventRole"])) {
            await this.dataChanged();
            await this.projectsChangedInternal();
        }           
    }

    async setWasOpened(): Promise<void> {
        this.wasOpened = true;
        await this.initCalendar();
    }

    async dataChanged(): Promise<void> {
        await this.service.getOutlineLevels();
        this.projects = await this.service.getProjects();
        let projectTypes = await this.service.getProjectTypes();

        let treeInput = Arrays.explode(this.projects, p => (p.structurePaths ?? []).map(sp => ({p, sp})));
        this.tree = GuiUtils.generateTree2(treeInput, true, false, (p, g, m) => [
            g("0" + p.p.typeId, () => m(projectTypes.find(pt => pt.id == p.p.typeId)?.title ?? "<???>", null, { expanded: false })),
            g("1" + p.p.id, () => m(p.p.title, p.p)),
            ...Arrays.getSubListsFromBeginning(Strings.split(p.sp, "|")).map(sp => 
                g("2" + p.p.id + "|" + Arrays.itemsToString(sp, "|"), () => m(this.service.formatStructurePathString(sp[sp.length - 1]), {p: p.p, sp: Arrays.itemsToString(sp, "|"), projectNumber: p.p.projectNumber})))
        ]);
    }

    projectContextMenu(evt: MouseEvent): void {
        const key = (evt.target as HTMLElement).closest("li.p-tree-node")?.getAttribute("data-id");

        if (key == null) {
            return;
        }

        const node = GuiUtils.flattenTree(this.tree).find(node2 => node2.key == key);

        if (node == null) {
            return;
        }

        evt.preventDefault();
        evt.stopPropagation();

        if (node.data instanceof Project) {
            this.app.openContextMenu(this.service.getProjectContextMenuItems(node.data, this.service.getCurrentUser(), this.projectsChanged.bind(this), this.projectsChanged.bind(this), this));
        }
    }

    debouncedProjectsChanged = Utils.debounce(async () => this.projectsChangedInternal(), 50);

    projectsChanged(): void {
        this.debouncedProjectsChanged();
    }

    async projectsChangedInternal(): Promise<void> {  
        let projects = Arrays.getUnique(Arrays.withoutNull(this.selectedProjects.map(tn => tn.data instanceof Project ? tn.data.id : tn.data?.p instanceof Project ? tn.data.p.id : null)));
        this.dtos = Arrays.withoutNull(await Promise.all(this.projects.filter(project => projects.includes(project.id)).map(async (project) => this.service.getProjectDto(project.id))));
        this.resources = [];
        for (let dto of this.dtos) {
            let ri: ResourceInput = { id: Objects.toString(dto.project.id), title: dto.project.title + " [" + dto.project.projectNumber + "]", children: [], extendedProps: { isProject: true } };
            this.resources.push(ri);
            let events = Arrays.sortBy(dto.events, e => e.startDate);
            for (let sp of dto.project.structurePaths) {
                if (!this.selectedProjects.some(tn => tn.data?.p?.id == dto.project.id && tn.data?.sp == sp)) {
                    continue;
                }

                let parentRi = ri;
                for (let subSp of Arrays.getSubListsFromBeginning(Strings.split(sp, "|"))) {
                    let id = Objects.toString(dto.project.id) + "^" + Arrays.itemsToString(subSp, "|");
                    let existingRi = parentRi.children?.find(c => c.id == id);
                    if (existingRi == null) {
                        existingRi = { id, title: this.service.formatStructurePathString(subSp[subSp.length - 1]), children: [], extendedProps: { isStructurePath: true } }; 
                        parentRi.children.push(existingRi);
                    }
                    parentRi = existingRi;
                }
                for (let event of events) {
                    if (event.structurePath == sp) {
                        parentRi.children.push({ id: Objects.toString(dto.project.id) + "^" + sp + "^" + event.id, title: event.title, extendedProps: { isEvent: true, firstEvent: event } });
                    }
                }
            }
        }
        await this.initCalendar();
    }

    async initCalendar(): Promise<void> {
        if (!this.wasOpened) {
            return;
        }

        let statusActive = this.form.getValueBool("statusActive");
        let statusFinished = this.form.getValueBool("statusFinished");
        let filteredEvents = Arrays.explode(this.dtos, dto => dto.events).filter(e =>  (statusActive || e.finished) && (statusFinished || !e.finished));
        await this.calendarWrapper.initCalendar(() => this.fc, filteredEvents, this.resources, this.projects.map(p => p.calendarId));     
    }    
}
